import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Select, MenuItem, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

// Font size menu -> the browser's <font size="n"> values. The backend maps
// these to points (richText.js FONT_SIZES), so keep the two in step.
const SIZES = [
  { value: '2', label: 'Small' },
  { value: '3', label: 'Normal' },
  { value: '5', label: 'Large' },
  { value: '6', label: 'Heading' },
];

const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Older plain-text templates ("- " bullets, blank lines) -> editor HTML.
export function plainToHtml(text) {
  const out = [];
  let open = 0; // open <ul> depth
  const setDepth = (d) => { while (open < d) { out.push('<ul>'); open += 1; } while (open > d) { out.push('</ul>'); open -= 1; } };
  String(text || '').replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    const m = line.match(/^(\s*)[-*•]\s+(.*)$/);
    if (m) {
      setDepth(m[1].replace(/\t/g, '  ').length >= 2 ? 2 : 1);
      out.push(`<li>${escapeHtml(m[2])}</li>`);
    } else {
      setDepth(0);
      out.push(line.trim() ? `<div>${escapeHtml(line)}</div>` : '<div><br></div>');
    }
  });
  setDepth(0);
  return out.join('');
}

const isHtml = (s) => /<\/?[a-z][^>]*>/i.test(String(s || ''));

// Saved templates come from the sheet, so before they're loaded into the
// editor everything but the toolbar's own formatting is stripped — no
// scripts, links, images, event handlers or other attributes. DOMParser
// builds an inert document (nothing in it runs) to read from.
const KEEP_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'DIV', 'P', 'BR', 'FONT', 'SPAN']);
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE', 'NOSCRIPT']);
function sanitizeEditorHtml(html) {
  const source = new DOMParser().parseFromString(String(html || ''), 'text/html').body;
  const out = document.createElement('div');
  const copy = (from, to) => {
    from.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) { to.appendChild(document.createTextNode(node.textContent)); return; }
      if (node.nodeType !== Node.ELEMENT_NODE || DROP_TAGS.has(node.tagName)) return;
      if (!KEEP_TAGS.has(node.tagName)) { copy(node, to); return; } // keep the text, drop the tag
      const el = document.createElement(node.tagName);
      if (node.tagName === 'FONT' && /^[1-7]$/.test(node.getAttribute('size') || '')) el.setAttribute('size', node.getAttribute('size'));
      to.appendChild(el);
      copy(node, el);
    });
  };
  copy(source, out);
  return out.innerHTML;
}

// A small rich-text box: bold, italic, underline, bullets and font size.
// Uncontrolled — initialValue is loaded once (remount with a new key to
// reload); every edit calls onChange(html). Pasted text comes in as plain
// text, so no outside formatting sneaks in. The ref exposes insertText()
// for the placeholder chips. Whatever HTML this produces, the backend only
// ever outputs it through its own parser (backend/richText.js).
const RichTextEditor = forwardRef(function RichTextEditor({ initialValue, onChange, minHeight = 320, label = 'Contract text' }, ref) {
  const boxRef = useRef(null);
  const rangeRef = useRef(null);
  const [active, setActive] = useState({ bold: false, italic: false, underline: false, list: false, size: '3' });

  useEffect(() => {
    if (boxRef.current) boxRef.current.innerHTML = isHtml(initialValue) ? sanitizeEditorHtml(initialValue) : plainToHtml(initialValue);
    // Loaded once per mount on purpose (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inEditor = (node) => !!node && !!boxRef.current && boxRef.current.contains(node);

  // Remember the selection while it's in the box, so toolbar clicks (and the
  // size menu, which takes focus) apply to it.
  const refreshState = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !inEditor(sel.anchorNode)) return;
    rangeRef.current = sel.getRangeAt(0).cloneRange();
    setActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      list: document.queryCommandState('insertUnorderedList'),
      size: String(document.queryCommandValue('fontSize') || '3'),
    });
  }, []);
  useEffect(() => {
    document.addEventListener('selectionchange', refreshState);
    return () => document.removeEventListener('selectionchange', refreshState);
  }, [refreshState]);

  const restore = () => {
    const box = boxRef.current;
    box.focus();
    const sel = window.getSelection();
    if (rangeRef.current && sel) { sel.removeAllRanges(); sel.addRange(rangeRef.current); }
  };
  const emit = () => onChange?.(boxRef.current.innerHTML);
  const run = (command, value) => {
    restore();
    document.execCommand('styleWithCSS', false, false); // <b>/<i>/<u>/<font>, not inline CSS
    document.execCommand(command, false, value);
    emit();
    refreshState();
  };

  useImperativeHandle(ref, () => ({
    insertText: (text) => run('insertText', text),
  }));

  const button = (key, title, Icon, command) => (
    <Tooltip title={title} key={key}>
      <IconButton size="small" color={active[key] ? 'primary' : 'default'} sx={active[key] ? { bgcolor: 'action.selected' } : undefined}
        onMouseDown={e => e.preventDefault() /* keep the selection */} onClick={() => run(command)}>
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:focus-within': { borderColor: 'primary.main' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        {button('bold', 'Bold (Ctrl+B)', FormatBoldIcon, 'bold')}
        {button('italic', 'Italic (Ctrl+I)', FormatItalicIcon, 'italic')}
        {button('underline', 'Underline (Ctrl+U)', FormatUnderlinedIcon, 'underline')}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {button('list', 'Bullet points', FormatListBulletedIcon, 'insertUnorderedList')}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Select size="small" variant="standard" disableUnderline value={SIZES.some(s => s.value === active.size) ? active.size : '3'}
          onChange={e => run('fontSize', e.target.value)} sx={{ fontSize: 13, minWidth: 90, px: 1 }}
          MenuProps={{ disableAutoFocusItem: true }}>
          {SIZES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13 }}>{s.label}</MenuItem>)}
        </Select>
      </Box>
      <Box ref={boxRef} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label={label}
        onInput={emit} onKeyUp={refreshState} onMouseUp={refreshState}
        onPaste={(e) => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
        sx={{
          minHeight, maxHeight: 520, overflowY: 'auto', p: 1.5, outline: 'none', typography: 'body2', lineHeight: 1.6,
          '& ul': { my: 0.5, pl: 3 }, '& font[size="2"]': { fontSize: '0.85em' }, '& font[size="5"]': { fontSize: '1.3em' },
          '& font[size="6"]': { fontSize: '1.6em' },
        }} />
    </Box>
  );
});

export default RichTextEditor;
